import React from 'react'
import styled from 'styled-components'
import { useAccountLock } from '../../../hooks/useAccountLock'
import { useAuth } from '../../../contexts/AuthContext/AuthContext'

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 48px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
`

const LockIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  background: ${props => props.color || '#dc3545'};
  color: white;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #333;
`

const ReasonBadge = styled.div`
  display: inline-block;
  background: ${props => props.color || '#6c757d'};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Message = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 32px;
`

const ContactInfo = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
`

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`

const Email = styled.a`
  color: #007bff;
  text-decoration: none;
  font-weight: 600;
  font-size: 18px;

  &:hover {
    text-decoration: underline;
  }
`

const LogoutButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #5a6268;
  }
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 12px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`

export const AccountLocked: React.FC = () => {
  const { signOut } = useAuth()
  const {
    lockStatus,
    loading,
    error,
    lockMessage,
    lockReason,
    contactEmail,
    getLockReasonDisplay,
    getLockReasonColor,
  } = useAccountLock()

  const handleLogout = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  if (loading) {
    return (
      <Container>
        <Card>
          <LoadingSpinner />
          <div>Checking account status...</div>
        </Card>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Card>
          <LockIcon color="#dc3545">🚫</LockIcon>
          <Title>Account Status Error</Title>
          <ErrorMessage>{error}</ErrorMessage>
          <LogoutButton onClick={handleLogout}>Sign Out</LogoutButton>
        </Card>
      </Container>
    )
  }

  if (!lockStatus?.isLocked) {
    // This shouldn't happen if the component is used correctly
    return null
  }

  const reasonDisplay = getLockReasonDisplay(lockReason)
  const reasonColor = getLockReasonColor(lockReason)

  return (
    <Container>
      <Card>
        <LockIcon color={reasonColor}>🔒</LockIcon>
        <Title>Account Locked</Title>

        <ReasonBadge color={reasonColor}>{reasonDisplay}</ReasonBadge>

        <Message>
          {lockMessage ||
            'Your account has been locked due to a policy violation. Please contact our support team for assistance.'}
        </Message>

        <ContactInfo>
          <ContactTitle>Need Help?</ContactTitle>
          <div>Contact our support team at:</div>
          <Email href={`mailto:${contactEmail}`}>{contactEmail}</Email>
        </ContactInfo>

        <LogoutButton onClick={handleLogout}>Sign Out & Exit</LogoutButton>
      </Card>
    </Container>
  )
}
